/* assets/wl__mixcalc.js
   Peptide mixing calculator — SHARED between the Weight Loss Hub
   (sections/wl-program.liquid) and the anti-aging collection
   (templates/collection.anti-aging.json). Edit doses HERE only:
   WL_MIX_ENABLED turns peptides on/off, WL_MIX_DOSE_OVERRIDE sets
   Reflections' conservative starting targets (mcg). */
(function () {
  function qs(sel, root) { return (root || document).querySelector(sel); }

  // Peptide mixing calculator (2026-07-17) — lives at the top of the
  // add-on shop. Vial mg + bacteriostatic water mL -> concentration,
  // units to draw on a U-100 syringe for the peptide's typical dose, and
  // doses per vial. Typical-dose data extracted from the Peptide Playbook
  // snapshot (30 injectables with community-cited doses) — reference-only
  // starting points, not a clinical protocol.
  // Only peptides Reflections actually carries show in the mixing
  // calculator dropdown. To enable more as the catalog grows, add the
  // exact name from WL_MIX_DATA below — nothing else to change.
  // Per-peptide caution notes shown in the blue info line.
  const WL_MIX_NOTE = {
    "MOTS-c": '<span style="color:#b3261e;font-weight:700;">The 3–4 mg dose is ONLY for people who have been on MOTS-c for more than a month AND are in good cardiovascular health. Everyone else stays at 0.5–1 mg.</span>',
    "Sermorelin": "Best to start with a HALF dose for your first 3 days and assess tolerance before moving to the full target.",
    "CJC-1295 / Ipamorelin Blend (dose by CJC)": "Best to start with a HALF dose for your first 3 days and assess tolerance before moving to the full target.",
    "Tesamorelin": "Best to start with a HALF dose for your first 3 days and assess tolerance before moving to the full target.",
    "Tesamorelin / Ipamorelin Blend (pre-mixed, dose by Tesamorelin)": "Best to start with a HALF dose for your first 3 days and assess tolerance before moving to the full target.",
    "TITAN Blend (pre-mixed, dose by Tesamorelin)": "Best to start with a HALF dose for your first 3 nights and assess tolerance before moving to the full target.",
    "SERENITY Blend (pre-mixed, dose by Selank)": "Best to start with a HALF dose for your first 3 nights — GH-axis components on board.",
    "SYNAPSE Blend (pre-mixed, dose by Tesamorelin)": "Best to start with a HALF dose for your first 3 days. Dose in the morning — can be stimulating.",
    "SHRED Blend (pre-mixed, dose by MOTS-c)": "Best to start with a HALF dose for your first 3 days. Dose in the morning, fasted — GH-axis components on board.",
    "IGNITE Blend (dose by MOTS-c)": "Best to start with a HALF dose for your first 3 days. Mornings, fasted — before exercise if you train. NAD+ can flush briefly; a slower push helps. No growth-hormone components.",
    "FUSION Blend (pre-mixed, dose by Tesamorelin)": "Best to start with a HALF dose for your first 3 days and assess tolerance.",
    "ASCEND Blend (pre-mixed, dose by Tesamorelin)": "Best to start with a HALF dose for your first 3 nights. Contains IGF-1 LR3 — flag any personal or family cancer history to your provider before starting.",
    "FORTIFY Blend (pre-mixed, dose by Thymosin Alpha-1)": "<span style=\"color:#b3261e;font-weight:700;\">Not for autoimmune conditions, immunosuppressant therapy, or transplant recipients — immune stimulation is the whole point of this product. Talk to your provider first.</span>",
    "Melanotan II (pre-mixed 10 mg)": "<span style=\"color:#b3261e;font-weight:700;\">Darkens existing moles — monitor your skin and show us any change. Start low: nausea and flushing are worst on the first injections.</span>",
    "Thymalin": "Thymalin is taken as a short course, not on an ongoing schedule. Read the product page or reach out to staff for dosing information.",
    "PT-141 (Bremelanotide)": "PT-141 is a melanocortin, like Melanotan — with frequent use it can tan the skin and darken existing moles. Keep it to 1–2 times per week unless the added pigmentation is wanted.",
  };

  // Reflections' conservative starting targets (mcg) — these win over
  // the community-midpoint prefill. Philosophy per Derek: start low, let
  // people dial up with their provider.
  const WL_MIX_DOSE_OVERRIDE = {
    "CJC-1295 / Ipamorelin Blend (dose by CJC)": 200,
    "Sermorelin": 300,
    "MOTS-c": 500,
    "CJC-1295 (No DAC)": 200,
    "Retatrutide (GLP)": 1500,
  };

  // -----------------------------------------------------------------
  // Reflections protocol autofill (Derek, 2026-08-11). One entry per
  // carried injectable: vial (mg), water (mL bacteriostatic), and three
  // dose tiers for the slider — Starting / Been on it / Need a higher
  // dose (mcg). perWeek converts doses/vial into days-of-supply
  // (7 = daily, 1 = weekly); asNeeded shows "uses per vial" instead.
  // Selecting a peptide autofills everything; changing water/vial/dose
  // away from protocol turns the doses+duration readout RED so members
  // see that deviating from our protocol changes how long a vial lasts.
  // Peptides WITHOUT an entry here keep the old prefill behavior.
  // PRIMARY DRIVER: the Starting tier should give ≥20 days per vial.
  // -----------------------------------------------------------------
  const WL_MIX_PROTOCOL = {
    "Tesamorelin":  { vial: 10, water: 2, perWeek: 7, tiers: [500, 750, 1000] },   // 20 / 13 / 10 days
    "Epitalon":     { vial: 50, water: 5, perWeek: 7, tiers: [750, 1000, 1250] },  // 66 / 50 / 40 days
    "BPC-157":      { vials: [{ mg: 10, water: 2 }, { mg: 20, water: 4 }], perWeek: 7, tiers: [250, 400, 500] }, // 10 mg: 40/25/20 d - 20 mg: 80/50/40 d (both 5 mg/mL, same draws)
    "Wolverine Blend (dose by BPC-157)": { vial: 10, water: 2, perWeek: 7, tiers: [250, 400, 500] },
    "GLOW Stack (dose by BPC-157)":      { vial: 10, water: 2, perWeek: 7, tiers: [250, 400, 500] },
    "CJC-1295 / Ipamorelin Blend (dose by CJC)": { vial: 5, water: 2, perWeek: 7, tiers: [200, 225, 250] }, // 25 / 22 / 20 days (capped at 250 per Derek 2026-08-11 - every tier passes the 20-day driver)
    "MOTS-c":       { vials: [{ mg: 10, water: 2 }, { mg: 40, water: 4 }], perWeek: 3, tiers: [500, 1000, 3000] }, // Derek 2026-08-11: 0.5-1 mg starting; 3-4 mg ONLY after 1+ month on it AND good cardiovascular health (red note below)
    "PT-141 (Bremelanotide)": { vial: 10, water: 2, asNeeded: true, tiers: [1000, 1500, 2000] }, // 10 / 6 / 5 uses
    "Retatrutide (GLP)": { vials: [{ mg: 30, water: 3 }, { mg: 60, water: 6 }], perWeek: 1, tiers: [1500, 3000, 6000] }, // Derek: 30 mg + 3 mL, 60 mg + 6 mL = 10 mg/mL both, easy math (draws 15/30/60 units)
    "Selank":       { vial: 10, water: 2, perWeek: 7, tiers: [250, 375, 500] },    // 40 / 26 / 20 days
    "Sermorelin":   { vial: 10, water: 3, perWeek: 5, tiers: [200, 300, 500] },    // 70 / 46 / 28 days at 5 days/week (Derek 2026-08-11)
    "AOD-9604":     { vial: 10, water: 2, perWeek: 7, tiers: [250, 400, 500] },    // 40 / 25 / 20 days (10 mg vials per Derek 2026-08-11)
    // PRE-MIXED (Derek 2026-08-11): vial/water lock to the bottle's real
    // numbers and grey out - the calculator becomes a DOSING calculator
    // (draw units + duration) with nothing to reconstitute.
    "NAD+":         { vial: 1000, water: 10, perWeek: 5, tiers: [20000, 35000, 50000], premixed: true }, // Derek: 20 mg x 5 days/wk to start, progression 20/35/50 mg per injection (draws 20/35/50 units; 50/28/20 doses per bottle)
    "Sermorelin (pre-mixed 15 mg)": { vial: 15, water: 5, perWeek: 5, tiers: [200, 300, 500], premixed: true }, // 105 / 70 / 42 days (pre-mixed 3 mg/mL, 5 mL bottle)
    "Tesamorelin / Ipamorelin Blend (pre-mixed, dose by Tesamorelin)": { vial: 15, water: 5, perWeek: 7, tiers: [500, 750, 1000], premixed: true }, // 30 / 20 / 15 days - pre-mixed 5 mL bottle at 3 mg/mL tesamorelin + 2 mg/mL ipamorelin; dosed by its tesamorelin content, same tiers Derek locked for Tesamorelin solo (draws 17 / 25 / 33 units)
    // ---- PREMIXED / DESIGNER LINE (2026-08-28, DOSING-LOCKS.md) ----
    "TITAN Blend (pre-mixed, dose by Tesamorelin)":    { vial: 10, water: 5, perWeek: 5, tiers: [300, 400, 500], premixed: true },   // 46/35/28 days
    "SERENITY Blend (pre-mixed, dose by Selank)":      { vial: 5,  water: 5, perWeek: 7, tiers: [150, 200, 250], premixed: true },   // 33/25/20 days, nightly
    "SYNAPSE Blend (pre-mixed, dose by Tesamorelin)":  { vial: 10, water: 5, perWeek: 5, tiers: [300, 400, 500], premixed: true },   // MOTS-c capped at 500 mcg
    "SHRED Blend (pre-mixed, dose by MOTS-c)":         { vial: 10, water: 5, perWeek: 5, tiers: [300, 400, 500], premixed: true },   // 46/35/28 days - AOD/CJC/Ipa/MOTS-c 2/1.2/2/2 mg-mL, MOTS-c constrains (as SYNAPSE)
    "IGNITE Blend (dose by MOTS-c)":                    { vial: 10, water: 2, perWeek: 5, tiers: [300, 400, 500] },                   // 46/35/28 days - NAD+ 100 / MOTS-c 10 / 5-Amino-1MQ 10 in 2 mL = 50/5/5 mg-mL; MOTS-c constrains (SHRED tiers). MIXABLE: Derek reconstitutes with 2 mL (2026-09-14)
    "FOCUS Blend (pre-mixed, dose by Semax)":          { vial: 5,  water: 5, perWeek: 5, tiers: [200, 250, 300], premixed: true },
    "ELEVATE Blend (pre-mixed, dose by BPC-157)":      { vial: 10, water: 5, perWeek: 7, tiers: [240, 320, 400], premixed: true },   // the GUT product
    "ETERNAL Blend (pre-mixed, dose by GHK-Cu)":       { vial: 50, water: 5, perWeek: 7, tiers: [1200, 1600, 2000], premixed: true },
    "EUPHORIA Blend (pre-mixed, dose by MOTS-c)":      { vial: 10, water: 5, perWeek: 5, tiers: [240, 320, 400], premixed: true },
    "FORTIFY Blend (pre-mixed, dose by Thymosin Alpha-1)": { vial: 5, water: 5, perWeek: 5, tiers: [200, 250, 300], premixed: true },
    "FUSION Blend (pre-mixed, dose by Tesamorelin)":   { vial: 15, water: 5, perWeek: 5, tiers: [500, 750, 1000], premixed: true },  // lands on Derek's Tesa locks
    "ASCEND Blend (pre-mixed, dose by Tesamorelin)":   { vial: 10, water: 5, perWeek: 5, tiers: [300, 400, 500], premixed: true },   // IGF-1 LR3 rides at 30-50 mcg
    "Glutathione (pre-mixed 6,000 mg)":                { vial: 6000, water: 30, perWeek: 2, tiers: [200000, 400000, 600000], premixed: true }, // 200-600 mg pushes
    "SLU-PP-332 (pre-mixed 10 mg)":                    { vial: 10, water: 5, perWeek: 5, tiers: [300, 400, 500], premixed: true },
    "SS-31 / Elamipretide (pre-mixed 300 mg)":         { vial: 300, water: 6, perWeek: 5, tiers: [5000, 10000, 15000], premixed: true }, // dosed in MG, 5-15 mg
    "Melanotan II (pre-mixed 10 mg)":                  { vial: 10, water: 5, perWeek: 3, tiers: [250, 500, 1000], premixed: true },
    "Semax":        { vial: 10, water: 5, perWeek: 5, tiers: [300, 400, 500], premixed: true },   // 46/35/28 days - standalone runs ABOVE the 200-300 inside FOCUS
    "5-Amino-1MQ":  { vials: [{ mg: 5, water: 5 }, { mg: 25, water: 5 }], perWeek: 5, tiers: [500, 1000, 1500], premixed: true }, // 25 mg: 60(cap)/35/23 d - the 5 mg is a TRIAL size and runs short by design
    "Thymosin Alpha-1": { vial: 30, water: 3, perWeek: 2, tiers: [800, 1600, 3200], premixed: true }, // 1.6 mg = the Zadaxin clinical dose; 30 mg / 3 mL = 10 mg/mL
    "CJC-1295 (No DAC)": { vial: 10, water: 2, perWeek: 7, tiers: [200, 250, 300] }, // 50 / 40 / 33 days (10 mg standalone vial per Derek; tiers mirror the blend)

  };
  const WL_MIX_TIER_LABELS = ["Starting", "Been on it", "Need a higher dose"];

  const WL_MIX_ENABLED = [
    "AOD-9604",
    "Wolverine Blend (dose by BPC-157)",
    "GLOW Stack (dose by BPC-157)",
    "BPC-157",
    "CJC-1295 (No DAC)",
    "CJC-1295 / Ipamorelin Blend (dose by CJC)",
    "Epitalon",
    "MOTS-c",
    "NAD+",
    "PT-141 (Bremelanotide)",
    "Retatrutide (GLP)",
    "Selank",
    "Sermorelin",
    "Sermorelin (pre-mixed 15 mg)",
    "TITAN Blend (pre-mixed, dose by Tesamorelin)",
    "SERENITY Blend (pre-mixed, dose by Selank)",
    "SYNAPSE Blend (pre-mixed, dose by Tesamorelin)",
    "SHRED Blend (pre-mixed, dose by MOTS-c)",
    "IGNITE Blend (dose by MOTS-c)",
    "FOCUS Blend (pre-mixed, dose by Semax)",
    "ELEVATE Blend (pre-mixed, dose by BPC-157)",
    "ETERNAL Blend (pre-mixed, dose by GHK-Cu)",
    "EUPHORIA Blend (pre-mixed, dose by MOTS-c)",
    "FORTIFY Blend (pre-mixed, dose by Thymosin Alpha-1)",
    "FUSION Blend (pre-mixed, dose by Tesamorelin)",
    "ASCEND Blend (pre-mixed, dose by Tesamorelin)",
    "Glutathione (pre-mixed 6,000 mg)",
    "SLU-PP-332 (pre-mixed 10 mg)",
    "SS-31 / Elamipretide (pre-mixed 300 mg)",
    "Melanotan II (pre-mixed 10 mg)",
    "Tesamorelin",
    "Tesamorelin / Ipamorelin Blend (pre-mixed, dose by Tesamorelin)",
    "Thymosin Alpha-1",
    "Semax",
    "Thymalin",
    "5-Amino-1MQ",
  ];

  const WL_MIX_DATA = [
   {
    "n": "AOD-9604",
    "lo": 250,
    "hi": 500,
    "d": "250–500 mcg/day (commonly cited)",
    "f": "Daily",
    "r": "Subcutaneous"
   },
   {
    "n": "ARA-290",
    "lo": 2000,
    "hi": 4000,
    "d": "~2–4 mg (commonly cited)",
    "f": "Daily / cycled",
    "r": "Subcutaneous"
   },
   {
    "n": "BPC-157",
    "lo": 250,
    "hi": 500,
    "d": "250–500 mcg (commonly cited)",
    "f": "1–2x daily",
    "r": "Subcutaneous or Oral"
   },
   {
    "n": "Cagrilintide",
    "lo": 300,
    "hi": 2400,
    "d": "0.3–2.4 mg/wk (trials)",
    "f": "Weekly",
    "r": "Subcutaneous"
   },
   {
    "n": "CJC-1295 (No DAC)",
    "lo": 100,
    "hi": 100,
    "d": "100 mcg per dose (commonly cited — NOT mg)",
    "f": "1–2x daily / 5 on 2 off",
    "r": "Subcutaneous"
   },
   {
    "n": "CJC-1295 (With DAC)",
    "lo": 1000,
    "hi": 2000,
    "d": "1–2 mg per week",
    "f": "1–2x weekly",
    "r": "Subcutaneous"
   },
   {
    "n": "DSIP",
    "lo": 100,
    "hi": 500,
    "d": "100–500 mcg (commonly cited)",
    "f": "Before bed",
    "r": "Subcutaneous"
   },
   {
    "n": "Epitalon",
    "lo": 5000,
    "hi": 10000,
    "d": "~1 mg/day — one 50 mg vial is about a 7-week course (Reflections protocol)",
    "f": "Daily",
    "r": "Subcutaneous"
   },
   {
    "n": "Follistatin 344",
    "lo": 100,
    "hi": 100,
    "d": "100 mcg (commonly cited)",
    "f": "Cycled",
    "r": "Subcutaneous"
   },
   {
    "n": "GHRP-2",
    "lo": 100,
    "hi": 300,
    "d": "100–300 mcg per dose (commonly cited)",
    "f": "1–3x daily",
    "r": "Subcutaneous"
   },
   {
    "n": "GHRP-6",
    "lo": 100,
    "hi": 300,
    "d": "100–300 mcg per dose",
    "f": "1–3x daily, cycled",
    "r": "Subcutaneous"
   },
   {
    "n": "Gonadorelin",
    "lo": 100,
    "hi": 200,
    "d": "100–200 mcg (commonly cited)",
    "f": "2–3x/week or per protocol",
    "r": "Subcutaneous"
   },
   {
    "n": "Hexarelin",
    "lo": 100,
    "hi": 100,
    "d": "100 mcg per dose",
    "f": "1–2x daily, short cycles only",
    "r": "Subcutaneous"
   },
   {
    "n": "IGF-1 LR3",
    "lo": 20,
    "hi": 50,
    "d": "20–50 mcg (commonly cited)",
    "f": "Daily / post-workout",
    "r": "Subcutaneous"
   },
   {
    "n": "Ipamorelin",
    "lo": 100,
    "hi": 300,
    "d": "100–300 mcg per dose (commonly cited)",
    "f": "1–3x daily",
    "r": "Subcutaneous"
   },
   {
    "n": "KPV",
    "lo": 200,
    "hi": 500,
    "d": "200–500 mcg (commonly cited)",
    "f": "1–2x daily",
    "r": "Subcutaneous or Oral"
   },
   {
    "n": "LL-37",
    "lo": 100,
    "hi": 500,
    "d": "100–500 mcg (commonly cited)",
    "f": "Daily / cycled",
    "r": "Subcutaneous"
   },
   {
    "n": "Melanotan II",
    "lo": 250,
    "hi": 500,
    "d": "250–500 mcg (commonly cited)",
    "f": "As needed / cycled",
    "r": "Subcutaneous"
   },
   {
    "n": "MOTS-c",
    "lo": 5000,
    "hi": 10000,
    "d": "5–10 mg (commonly cited)",
    "f": "2–3x/week, cycled",
    "r": "Subcutaneous"
   },
   {
    "n": "NAD+",
    "lo": 100000,
    "hi": 500000,
    "d": "20 units (20 mg) per day, 5 days a week — 100 mg per week from the pre-mixed 1,000 mg bottle",
    "f": "5 days/week",
    "r": "Subcutaneous"
   },
   {
    "n": "PT-141 (Bremelanotide)",
    "lo": 1000,
    "hi": 2000,
    "d": "1–2 mg as needed (approved 1.75 mg)",
    "f": "As needed before activity — max 1–2×/week",
    "r": "Subcutaneous"
   },
   {
    "n": "Retatrutide (GLP)",
    "lo": 1000,
    "hi": 12000,
    "d": "~1–12 mg/wk, titrated (trial-based)",
    "f": "Weekly",
    "r": "Subcutaneous"
   },
   {
    "n": "Selank",
    "lo": 250,
    "hi": 500,
    "d": "250–500 mcg (often intranasal)",
    "f": "Daily / as needed",
    "r": "Intranasal or SubQ"
   },
   {
    "n": "Semax",
    "lo": 300,
    "hi": 600,
    "d": "300–600 mcg (often intranasal)",
    "f": "Daily",
    "r": "Intranasal or SubQ"
   },
   {
    "n": "Sermorelin",
    "lo": 100,
    "hi": 300,
    "d": "100–300 mcg per dose",
    "f": "5 days/week, before bed",
    "r": "Subcutaneous"
   },
   {
    "n": "SS-31 (Elamipretide)",
    "lo": 5000,
    "hi": 40000,
    "d": "~5–40 mg (trials)",
    "f": "Daily / cycled",
    "r": "Subcutaneous"
   },
   {
    "n": "TB-500 (Thymosin Beta-4)",
    "lo": 2000,
    "hi": 2500,
    "d": "2–2.5 mg per dose (commonly cited)",
    "f": "2x/week loading, then weekly",
    "r": "Subcutaneous"
   },
   {
    "n": "Tesamorelin",
    "lo": 1000,
    "hi": 2000,
    "d": "0.5–1 mg/day (Reflections protocol; approved use is 2 mg/day)",
    "f": "Daily",
    "r": "Subcutaneous"
   },
   {
    "n": "Thymalin",
    "lo": 5000,
    "hi": 10000,
    "d": "5–10 mg per day",
    "f": "Once daily for 10 days, then stop; repeat the 10-day course a few times a year",
    "r": "Subcutaneous"
   },
   {
    "n": "Thymosin Alpha-1",
    "lo": 1600,
    "hi": 1600,
    "d": "1.6 mg per dose — comes pre-mixed at 10 mg/mL, nothing to reconstitute",
    "f": "Twice a week; during an active illness some use it daily for 5–7 days, then stop",
    "r": "Subcutaneous",
    "v": 30
   }
  ];

  // Catalog blends — dosed by their BPC-157 content per Derek (each vial
  // carries 10 mg BPC): enter 10 as "peptide in vial" (prefilled via v)
  // and the units math tracks the BPC dose.
  WL_MIX_DATA.push(
    { n: "CJC-1295 / Ipamorelin Blend (dose by CJC)", lo: 100, hi: 100, d: "100 mcg per dose (commonly cited — NOT mg)", f: "Nightly", r: "Subcutaneous", v: 5 },
    { n: "Wolverine Blend (dose by BPC-157)", lo: 250, hi: 500, d: "dose by its BPC-157 content — 10 mg per vial (prefilled)", f: "1–2x daily", r: "Subcutaneous", v: 10 },
    { n: "GLOW Stack (dose by BPC-157)", lo: 250, hi: 500, d: "dose by its BPC-157 content — 10 mg per vial (prefilled)", f: "1–2x daily", r: "Subcutaneous", v: 10 }
,
    { n: "Sermorelin (pre-mixed 15 mg)", lo: 200, hi: 500, d: "200–500 mcg per dose — comes pre-mixed at 3 mg/mL, nothing to reconstitute", f: "5 days/week, before bed", r: "Subcutaneous" },
    { n: "Tesamorelin / Ipamorelin Blend (pre-mixed, dose by Tesamorelin)", lo: 500, hi: 1000, d: "0.5–1 mg tesamorelin per dose (Reflections protocol) — comes pre-mixed at 3 mg/mL tesamorelin + 2 mg/mL ipamorelin, nothing to reconstitute", f: "Daily", r: "Subcutaneous", v: 15 },
    // ---- PREMIXED / DESIGNER LINE (2026-08-28, DOSING-LOCKS.md). The
    // dropdown renders from WL_MIX_DATA; WL_MIX_PROTOCOL locks vial/water
    // and the tier buttons once one is selected. ----
    { n: "TITAN Blend (pre-mixed, dose by Tesamorelin)", lo: 300, hi: 500, d: "300–500 mcg tesamorelin per dose — pre-mixed 5 mL, dosed by its tesamorelin side", f: "5 nights/week", r: "Subcutaneous", v: 10 },
    { n: "SERENITY Blend (pre-mixed, dose by Selank)", lo: 150, hi: 250, d: "150–250 mcg Selank/DSIP per dose — pre-mixed sleep stack", f: "Nightly, as needed", r: "Subcutaneous", v: 5 },
    { n: "SYNAPSE Blend (pre-mixed, dose by Tesamorelin)", lo: 300, hi: 500, d: "300–500 mcg tesamorelin per dose — mornings, can be stimulating", f: "5 days/week", r: "Subcutaneous", v: 10 },
    { n: "SHRED Blend (pre-mixed, dose by MOTS-c)", lo: 300, hi: 500, d: "300–500 mcg MOTS-c per dose — the cutting stack, mornings fasted", f: "5 days/week", r: "Subcutaneous", v: 10 },
    { n: "IGNITE Blend (dose by MOTS-c)", lo: 300, hi: 500, d: "300–500 mcg MOTS-c per dose — the no-GH cutting stack (NAD+ and 5-Amino-1MQ ride along), mornings fasted", f: "5 days/week", r: "Subcutaneous", v: 10 },
    { n: "FOCUS Blend (pre-mixed, dose by Semax)", lo: 200, hi: 300, d: "200–300 mcg each of Semax and Selank per dose", f: "5 days/week", r: "Subcutaneous", v: 5 },
    { n: "ELEVATE Blend (pre-mixed, dose by BPC-157)", lo: 240, hi: 400, d: "240–400 mcg BPC-157/KPV per dose — the gut pair sets the schedule", f: "Daily", r: "Subcutaneous", v: 10 },
    { n: "ETERNAL Blend (pre-mixed, dose by GHK-Cu)", lo: 1200, hi: 2000, d: "1.2–2 mg GHK-Cu per dose — skin & longevity stack", f: "Daily", r: "Subcutaneous", v: 50 },
    { n: "EUPHORIA Blend (pre-mixed, dose by MOTS-c)", lo: 240, hi: 400, d: "240–400 mcg MOTS-c per dose", f: "5 days/week", r: "Subcutaneous", v: 10 },
    { n: "FORTIFY Blend (pre-mixed, dose by Thymosin Alpha-1)", lo: 200, hi: 300, d: "200–300 mcg Thymosin Alpha-1 per dose — the winter immune stack", f: "5 days/week", r: "Subcutaneous", v: 5 },
    { n: "FUSION Blend (pre-mixed, dose by Tesamorelin)", lo: 500, hi: 1000, d: "0.5–1 mg tesamorelin per dose — the metabolic stack", f: "5 days/week", r: "Subcutaneous", v: 15 },
    { n: "ASCEND Blend (pre-mixed, dose by Tesamorelin)", lo: 300, hi: 500, d: "300–500 mcg tesamorelin (+30–50 mcg IGF-1 LR3) per dose", f: "5 nights/week", r: "Subcutaneous", v: 10 },
    { n: "Glutathione (pre-mixed 6,000 mg)", lo: 200000, hi: 600000, d: "200–600 mg per push — pre-mixed 200 mg/mL, larger draws than a peptide", f: "2x/week", r: "Subcutaneous or slow IM", v: 6000 },
    { n: "SLU-PP-332 (pre-mixed 10 mg)", lo: 300, hi: 500, d: "300–500 mcg per dose on the 10 mg vial", f: "5 days/week", r: "Subcutaneous", v: 10 },
    { n: "SS-31 / Elamipretide (pre-mixed 300 mg)", lo: 5000, hi: 15000, d: "5–15 mg per dose — SS-31 doses in MILLIGRAMS, pre-mixed 50 mg/mL", f: "5 days/week", r: "Subcutaneous", v: 300 },
    { n: "Melanotan II (pre-mixed 10 mg)", lo: 250, hi: 1000, d: "250 mcg to start; 500 mcg–1 mg established — darkens moles, monitor your skin", f: "3x/week", r: "Subcutaneous", v: 10 },
    { n: "5-Amino-1MQ", lo: 500, hi: 1500, d: "0.5–1.5 mg per dose on the 25 mg vial — the 5 mg vial is a trial size and runs short", f: "5 days/week", r: "Subcutaneous", v: 25 }
  );

  function initMixCalc() {
    const sel = qs("[data-wl-mix-peptide]");
    if (!sel) return;

    WL_MIX_DATA.forEach((p, i) => {
      if (WL_MIX_ENABLED.indexOf(p.n) === -1) return;
      const opt = document.createElement("option");
      opt.value = String(i);
      opt.textContent = p.n;
      sel.appendChild(opt);
    });

    const vialEl = qs("[data-wl-mix-vial]");
    const waterEl = qs("[data-wl-mix-water]");
    const doseEl = qs("[data-wl-mix-dose]");
    const infoEl = qs("[data-wl-mix-doseinfo]");
    const outEl = qs("[data-wl-mix-results]");

    // mg | mcg toggle (Derek 2026-08-28). INTERNALS STAY IN MCG - this is a
    // display layer only. readDoseMcg/writeDose are the ONLY places the
    // unit conversion happens; everything else keeps talking micrograms.
    let doseUnit = "mcg";
    try { doseUnit = localStorage.getItem("wl_mix_dose_unit") === "mg" ? "mg" : "mcg"; } catch (e) {}
    function readDoseMcg() {
      const v = parseFloat(doseEl && doseEl.value);
      if (!isFinite(v)) return NaN;
      return doseUnit === "mg" ? v * 1000 : v;
    }
    function writeDose(mcg) {
      if (!doseEl) return;
      doseEl.value = doseUnit === "mg" ? (Math.round(mcg) / 1000) : mcg;
    }
    function doseLabelEl() {
      // the label immediately preceding the dose field on every surface
      let n = doseEl && doseEl.closest("div");
      return n ? n.parentElement && n.parentElement.querySelector("label") || n.querySelector("label") : null;
    }
    function paintUnitUI() {
      const lab = (function () {
        let n = doseEl;
        while (n && n !== document.body) {
          const l = n.querySelector && n.querySelector("label");
          if (l && /target dose/i.test(l.textContent)) return l;
          n = n.parentElement;
        }
        return null;
      })();
      if (lab) lab.textContent = "Target dose (" + doseUnit + ")";
      if (doseEl) {
        doseEl.step = doseUnit === "mg" ? "0.05" : "1";
        doseEl.placeholder = doseUnit === "mg" ? "e.g., 0.375" : "e.g., 375";
      }
      qsa("[data-wl-mix-unit]").forEach(function (b) {
        const on = b.getAttribute("data-wl-mix-unit") === doseUnit;
        b.style.background = on ? "#122a44" : "#fff";
        b.style.color = on ? "#fff" : "#122a44";
        b.style.fontWeight = "700";
      });
    }
    function qsa(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
    if (doseEl && !qs("[data-wl-mix-unitwrap]")) {
      const w = document.createElement("div");
      w.setAttribute("data-wl-mix-unitwrap", "1");
      w.style.cssText = "display:flex;align-items:center;gap:6px;margin-top:6px;font-size:13px;color:#475569;";
      w.innerHTML = 'Enter the dose in: ' +
        '<button type="button" data-wl-mix-unit="mcg" style="border:1.5px solid #122a44;padding:4px 12px;cursor:pointer;font:inherit;border-radius:0;">mcg</button>' +
        '<button type="button" data-wl-mix-unit="mg" style="border:1.5px solid #122a44;padding:4px 12px;cursor:pointer;font:inherit;border-radius:0;">mg</button>';
      doseEl.insertAdjacentElement("afterend", w);

      // REVERSE LANE (Derek 2026-08-28: "calculate from units to
      // milligrams" - the tool must go both directions). Type the units on
      // your syringe and the dose field back-computes through the current
      // concentration; type a dose and the units field follows. A syncing
      // flag stops the two inputs from ping-ponging.
      // DIRECTION SELECTOR (Derek 2026-08-28: "show people what they are
      // injecting in milligrams if they only know their units... another
      // selector, so they can direct which way they're trying to go").
      // "dose" = I know my dose, tell me what to draw (the original tool);
      // "units" = I know my units, tell me what I'm injecting.
      let mixMode = "dose";
      try { mixMode = localStorage.getItem("wl_mix_mode") === "units" ? "units" : "dose"; } catch (e) {}
      const mw = document.createElement("div");
      mw.setAttribute("data-wl-mix-modewrap", "1");
      // grid-column 1/-1: the dose row sits inside a 12-column CSS grid on
      // the standalone page - without a span the selector collapses into one
      // skinny auto column and the labels stack letter-by-letter.
      mw.style.cssText = "display:flex;gap:0;margin:0 0 4px;border:1.5px solid #122a44;grid-column:1 / -1;width:100%;box-sizing:border-box;";
      mw.innerHTML =
        '<button type="button" data-wl-mix-mode="dose" style="flex:1;border:0;padding:10px 8px;cursor:pointer;font:inherit;font-size:13.5px;font-weight:700;border-radius:0;">I know my dose<br><span style="font-weight:400;font-size:12px;">show me what to draw</span></button>' +
        '<button type="button" data-wl-mix-mode="units" style="flex:1;border:0;padding:10px 8px;cursor:pointer;font:inherit;font-size:13.5px;font-weight:700;border-radius:0;">I know my units<br><span style="font-weight:400;font-size:12px;">show me what I’m injecting in milligrams</span></button>';
      const doseRow = doseEl.closest("div[style*='grid-column']") || doseEl.parentElement;
      (doseRow.parentElement || doseRow).insertBefore(mw, doseRow);

      const uw = document.createElement("div");
      uw.setAttribute("data-wl-mix-unitswrap", "1");
      uw.style.cssText = "margin-top:10px;padding:10px 12px;border:1px dashed #ad8952;background:#fdfaf4;grid-column:1 / -1;box-sizing:border-box;";
      uw.innerHTML = '<label style="display:block;font-weight:700;color:#122a44;font-size:13.5px;margin-bottom:4px;">How many units do you draw per injection?</label>' +
        '<input type="number" min="0" step="0.5" inputmode="decimal" data-wl-mix-units placeholder="e.g., 25" ' +
        'style="width:100%;box-sizing:border-box;padding:9px 11px;border:1px solid #cbd5e1;border-radius:0;font-size:16px;">' +
        '<div data-wl-mix-units-note style="font-size:12.5px;color:#6b7280;margin-top:4px;">Units convert using the concentration above (100 units = 1 mL).</div>';
      w.insertAdjacentElement("afterend", uw);
      const unitsInEl = uw.querySelector("[data-wl-mix-units]");
      let syncing = false;
      function concNow() {
        const vialMg = parseFloat(vialEl && vialEl.value);
        const waterMl = parseFloat(waterEl && waterEl.value);
        return (isFinite(vialMg) && vialMg > 0 && isFinite(waterMl) && waterMl > 0) ? vialMg / waterMl : NaN;
      }
      function syncUnitsFromDose() {
        if (syncing || !unitsInEl) return;
        const conc = concNow(), mcg = readDoseMcg();
        syncing = true;
        unitsInEl.value = (isFinite(conc) && isFinite(mcg) && mcg > 0)
          ? Math.round(((mcg / 1000) / conc) * 100 * 10) / 10 : "";
        syncing = false;
      }
      unitsInEl.addEventListener("input", function () {
        if (syncing) return;
        const conc = concNow();
        const note = uw.querySelector("[data-wl-mix-units-note]");
        if (!isFinite(conc)) {
          if (note) note.textContent = "Enter the vial mg and water mL first - units mean nothing without a concentration.";
          return;
        }
        const u = parseFloat(unitsInEl.value);
        if (!isFinite(u) || u <= 0) return;
        const mcg = (u / 100) * conc * 1000;
        syncing = true;
        writeDose(Math.round(mcg));
        syncing = false;
        if (note) note.textContent = u + " units at " + (Math.round(conc * 100) / 100) + " mg/mL = " +
          (mcg >= 1000 ? (Math.round(mcg) / 1000) + " mg" : Math.round(mcg) + " mcg") + " per injection.";
        compute();
      });
      doseEl.addEventListener("input", syncUnitsFromDose);
      [vialEl, waterEl].forEach(function (el) { el && el.addEventListener("input", syncUnitsFromDose); });
      window.__wlMixSyncUnits = syncUnitsFromDose;

      function applyMode() {
        window.__wlMixMode = mixMode;
        // dose mode: dose field + its mcg/mg toggle up front, units box hidden.
        // units mode: the units box IS the input; the dose row hides (it still
        // carries the canonical value underneath).
        doseRow.style.display = mixMode === "dose" ? "" : "none";
        w.style.display = mixMode === "dose" ? "" : "none";
        uw.style.display = mixMode === "units" ? "" : "none";
        if (mixMode === "units") uw.style.marginTop = "0";
        mw.querySelectorAll("[data-wl-mix-mode]").forEach(function (x) {
          const on = x.getAttribute("data-wl-mix-mode") === mixMode;
          x.style.background = on ? "#122a44" : "#fff";
          x.style.color = on ? "#fff" : "#122a44";
        });
      }
      mw.addEventListener("click", function (e) {
        const b2 = e.target.closest("[data-wl-mix-mode]");
        if (!b2) return;
        mixMode = b2.getAttribute("data-wl-mix-mode") === "units" ? "units" : "dose";
        try { localStorage.setItem("wl_mix_mode", mixMode); } catch (e2) {}
        applyMode();
        compute();
      });
      applyMode();
      window.__wlMixMode = mixMode;
      w.addEventListener("click", function (e) {
        const b = e.target.closest("[data-wl-mix-unit]");
        if (!b) return;
        const next = b.getAttribute("data-wl-mix-unit");
        if (next === doseUnit) return;
        const mcg = readDoseMcg();           // read in the OLD unit
        doseUnit = next;
        try { localStorage.setItem("wl_mix_dose_unit", doseUnit); } catch (e2) {}
        if (isFinite(mcg)) writeDose(mcg);   // repaint the same dose in the NEW unit
        paintUnitUI();
        compute();
      });
    }
    paintUnitUI();

    // The #1 point of confusion: vial sizes are in mg, doses in mcg.
    // Always-visible converter line with a worked example.
    if (outEl && !qs("[data-wl-mix-convert]")) {
      const conv = document.createElement("div");
      conv.setAttribute("data-wl-mix-convert", "1");
      conv.style.cssText = "font-size:12.5px;color:#475569;background:rgba(18,42,68,.10);border:1px solid rgba(18,42,68,.35);border-radius:8px;padding:7px 10px;margin:8px 0;line-height:1.5;";
      conv.innerHTML = "💡 <strong>mcg vs. mg:</strong> 1,000 mcg = 1 mg. Example: a 250 mcg dose = 0.25 mg — so a 10 mg vial holds 10,000 mcg, enough for 40 doses of 250 mcg.";
      outEl.parentNode.insertBefore(conv, outEl);
    }

    // ---- Protocol slider (injected once, shown only for protocol peptides) ----
    let activeProtocol = null;   // WL_MIX_PROTOCOL entry for current selection
    let activeTier = 0;          // 0 = Starting, 1 = Been on it, 2 = Need a higher dose
    let activeVariant = 0;       // index into the peptide's vial-size list
    let sliderWrap = null, sliderEl = null, sliderLabelEl = null, variantWrap = null;

    // Every peptide is normalized to a list of vial sizes; most have one.
    function protoVials(proto) {
      return proto.vials || [{ mg: proto.vial, water: proto.water }];
    }

    function ensureSlider() {
      if (sliderWrap || !sel) return;
      sliderWrap = document.createElement("div");
      sliderWrap.setAttribute("data-wl-mix-slider-wrap", "1");
      sliderWrap.style.cssText = "display:none;grid-column:1 / -1;margin:10px 0 4px;padding:12px 14px;border:1px solid rgba(18,42,68,.25);background:rgba(18,42,68,.05);";
      sliderWrap.innerHTML =
        '<div style="font-weight:700;font-size:13.5px;color:#122a44;line-height:1.5;">Where are you in your journey? ' +
          '<span data-wl-mix-tier-label style="font-size:12.5px;color:#7a6039;white-space:nowrap;"></span>' +
        '</div>' +
        '<input type="range" min="0" max="2" step="1" value="0" data-wl-mix-tier aria-label="Dose level" style="width:100%;margin:8px 0 2px;accent-color:#ad8952;">' +
        '<div style="display:flex;font-size:11.5px;color:#6b7280;line-height:1.3;">' +
          '<span style="flex:1;text-align:left;">Starting</span>' +
          '<span style="flex:1;text-align:center;">Been on it</span>' +
          '<span style="flex:1;text-align:right;">Need a<br>higher dose</span>' +
        '</div>';
      const anchor = sel.closest("div") || sel.parentNode;
      anchor.parentNode.insertBefore(sliderWrap, anchor.nextSibling);
      variantWrap = document.createElement("div");
      variantWrap.setAttribute("data-wl-mix-variants", "1");
      variantWrap.style.cssText = "display:none;gap:8px;margin:0 0 10px;flex-wrap:wrap;align-items:center;";
      sliderWrap.insertBefore(variantWrap, sliderWrap.firstChild);
      sliderEl = sliderWrap.querySelector("[data-wl-mix-tier]");
      sliderLabelEl = sliderWrap.querySelector("[data-wl-mix-tier-label]");
      sliderEl.addEventListener("input", () => {
        activeTier = parseInt(sliderEl.value, 10) || 0;
        applyProtocol();
      });
    }

    function fmtDose(mcg) {
      return mcg >= 1000 ? (Math.round(mcg / 100) / 10) + " mg" : mcg + " mcg";
    }

    function setPremixedLock(locked) {
      [vialEl, waterEl].forEach(function (el) {
        if (!el) return;
        el.disabled = locked;
        el.style.background = locked ? "#eef0f4" : "";
        el.style.color = locked ? "#6b7280" : "";
        el.title = locked ? "This product comes pre-mixed — these numbers are fixed by the bottle." : "";
      });
    }

    function renderVariantPicker() {
      if (!variantWrap || !activeProtocol) return;
      const vials = protoVials(activeProtocol);
      if (vials.length < 2) { variantWrap.style.display = "none"; return; }
      variantWrap.style.display = "flex";
      variantWrap.innerHTML = '<span style="font-size:12.5px;font-weight:700;color:#122a44;">Which vial did you buy?</span>';
      vials.forEach(function (v, i) {
        const b = document.createElement("button");
        b.type = "button";
        const on = i === activeVariant;
        b.style.cssText = "cursor:pointer;font-size:12.5px;padding:5px 12px;border:1px solid " +
          (on ? "#ad8952" : "#c9cdd4") + ";background:" + (on ? "#fbf7f1" : "#fff") +
          ";color:#122a44;font-weight:" + (on ? "700" : "400") + ";";
        b.textContent = v.mg + " mg vial";
        b.addEventListener("click", function () { activeVariant = i; applyProtocol(); });
        variantWrap.appendChild(b);
      });
    }

    function applyProtocol() {
      if (!activeProtocol) return;
      const v = protoVials(activeProtocol)[activeVariant] || protoVials(activeProtocol)[0];
      const doseMcg = activeProtocol.tiers[activeTier];
      setPremixedLock(!!activeProtocol.premixed);
      renderVariantPicker();
      if (vialEl) vialEl.value = v.mg;
      if (waterEl) waterEl.value = v.water;
      writeDose(doseMcg);
      if (window.__wlMixSyncUnits) window.__wlMixSyncUnits();
      if (sliderLabelEl) sliderLabelEl.textContent = WL_MIX_TIER_LABELS[activeTier] + " · " + fmtDose(doseMcg);
      compute();
    }

    // True when any input differs from the protocol autofill — the signal
    // to paint the doses/duration readout red (Derek: members must see that
    // changing our protocol changes how long the vial lasts).
    function protocolDeviation() {
      if (!activeProtocol) return false;
      const vial = parseFloat(vialEl && vialEl.value);
      const water = parseFloat(waterEl && waterEl.value);
      const dose = readDoseMcg();
      // Any vial size we actually sell (with its matching water) is protocol.
      const variantOk = protoVials(activeProtocol).some(function (v) {
        return vial === v.mg && water === v.water;
      });
      return !variantOk || activeProtocol.tiers.indexOf(dose) === -1;
    }

    sel.addEventListener("change", () => {
      const p = WL_MIX_DATA[parseInt(sel.value, 10)];
      ensureSlider();
      activeProtocol = p ? WL_MIX_PROTOCOL[p.n] || null : null;
      activeTier = 0;
      activeVariant = 0;
      setPremixedLock(!!(activeProtocol && activeProtocol.premixed));
      if (sliderWrap) sliderWrap.style.display = activeProtocol ? "block" : "none";
      if (sliderEl) sliderEl.value = "0";

      if (p && activeProtocol) {
        applyProtocol();
        if (infoEl) {
          var pline = "Our protocol: " + fmtDose(activeProtocol.tiers[0]) + " to start" + (p.f ? " · " + p.f : "") + (p.r ? " · " + p.r : "");
          if (activeProtocol.premixed) pline = "PRE-MIXED — draw straight from the bottle, nothing to reconstitute.<br>" + pline;
          if (WL_MIX_NOTE[p.n]) pline += "<br>⚠ " + WL_MIX_NOTE[p.n];
          pline += '<br><button type="button" data-wl-open-chat="1" style="all:unset;cursor:pointer;text-decoration:underline;color:#7a6039;font-weight:700;">💬 Questions? Chat with staff</button>';
          infoEl.innerHTML = pline;
        }
        return;
      }

      if (p) {
        writeDose(WL_MIX_DOSE_OVERRIDE[p.n] || Math.round((p.lo + p.hi) / 2));
        if (window.__wlMixSyncUnits) window.__wlMixSyncUnits();
        if (p.v && vialEl) vialEl.value = p.v;
        if (infoEl) {
          var line = (WL_MIX_DOSE_OVERRIDE[p.n] ? "Our conservative starting target — community range: " : "Typical: ") + p.d + (p.f ? " · " + p.f : "") + (p.r ? " · " + p.r : "");
          if (WL_MIX_NOTE[p.n]) line += "<br>⚠ " + WL_MIX_NOTE[p.n];
          line += '<br><button type="button" data-wl-open-chat="1" style="all:unset;cursor:pointer;text-decoration:underline;color:#7a6039;font-weight:700;">💬 Questions? Chat with staff</button>';
          infoEl.innerHTML = line;
        }
      } else if (infoEl) {
        infoEl.textContent = "";
      }
      compute();
    });

    function compute() {
      const vialMg = parseFloat(vialEl.value);
      const waterMl = parseFloat(waterEl.value);
      const doseMcg = readDoseMcg();
      if (!outEl) return;
      if (!isFinite(vialMg) || vialMg <= 0 || !isFinite(waterMl) || waterMl <= 0) {
        outEl.innerHTML = '<div class="wl-help" style="margin:0;">Enter your vial size and how much bacteriostatic water you\u2019re adding.</div>';
        return;
      }
      const conc = vialMg / waterMl; // mg per mL
      let html = '<div style="font-size:14px;"><strong>Concentration:</strong> ' + (Math.round(conc * 100) / 100) + " mg/mL</div>";
      if (isFinite(doseMcg) && doseMcg > 0) {
        const doseMg = doseMcg / 1000;
        const units = (doseMg / conc) * 100;
        const perVial = Math.floor(vialMg / doseMg);
        const unitsR = Math.round(units * 10) / 10;
        // In units mode the person is DESCRIBING their current habit, not
        // deviating from our protocol - the red warning would scold them for
        // answering the question we asked. Dose mode keeps it.
        const deviated = window.__wlMixMode === "units" ? false : protocolDeviation();
        const supplyColor = deviated ? "#b3261e" : "#6b7280";
        const supplyWeight = deviated ? "700" : "400";
        let supply = "~" + perVial + " doses per vial";
        if (activeProtocol) {
          if (activeProtocol.asNeeded) {
            supply = "~" + perVial + " uses per vial (as-needed peptide)";
          } else if (activeProtocol.perWeek) {
            // 60-DAY CEILING (Derek 2026-08-11): never describe any vial as
            // lasting longer than 60 days, no matter what the math says.
            const rawDays = Math.floor(perVial / activeProtocol.perWeek * 7);
            supply = "~" + perVial + " doses per vial · " +
              (rawDays > 60 ? "lasts up to 60 days at this pace" : "lasts about " + rawDays + " days at this pace");
          }
        }
        const mgR = Math.round(doseMg * 1000) / 1000;
        if (window.__wlMixMode === "units") {
          // units mode: they told us the draw - the ANSWER is the dose.
          html +=
            '<div style="font-size:17px;font-weight:900;margin-top:6px;">You’re injecting ' + mgR + " mg</div>" +
            '<div style="font-size:13px;color:#6b7280;">(' + Math.round(doseMcg) + " mcg) per injection — " + unitsR + " units at " + (Math.round(conc * 100) / 100) + " mg/mL</div>" +
            '';
        } else {
        html +=
          '<div style="font-size:17px;font-weight:900;margin-top:6px;">Draw ' + unitsR + " units</div>" +
          '<div style="font-size:13px;color:#6b7280;">on a standard U-100 insulin syringe per ' + doseMcg + " mcg (= " + mgR + " mg) dose</div>";
        }
        html +=
          '<div style="font-size:13.5px;color:' + supplyColor + ';font-weight:' + supplyWeight + ';margin-top:4px;">' + supply + "</div>";
        if (deviated) {
          html += '<div style="color:#b3261e;font-size:12.5px;margin-top:4px;">⚠ You’ve changed our recommended protocol — the doses and duration above reflect YOUR numbers, not ours. <button type="button" data-wl-mix-reset style="all:unset;cursor:pointer;text-decoration:underline;font-weight:700;color:#b3261e;">Reset to protocol</button></div>';
        }
        if (units > 100) {
          html += '<div style="color:#b45309;font-size:13px;margin-top:6px;">⚠ More than a full 1 mL syringe — use less bacteriostatic water for a stronger mix.</div>';
        } else if (units < 2) {
          html += '<div style="color:#b45309;font-size:13px;margin-top:6px;">⚠ Very small draw — add more bacteriostatic water so the dose is easier to measure.</div>';
        }
      }
      outEl.innerHTML = html;
    }

    if (outEl) outEl.addEventListener("click", (e) => {
      if (e.target.closest("[data-wl-mix-reset]")) applyProtocol();
    });

    [vialEl, waterEl, doseEl].forEach((el) => el && el.addEventListener("input", compute));
    compute();
  }


  function bootMixCalc() { initMixCalc(); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bootMixCalc);
  else bootMixCalc();
})();
